import { Helmet } from "@dr.pogodin/react-helmet";
import { cn } from "@tine/designsystem-utils";
import { useEffect } from "react";
import { useLocation } from "wouter";
import { PROMOTION_SECTION_SLUG } from "../../../account/promotions/PromotionPage";
import type { ContentfulArticle, EmbeddedEntryContextType } from "../../../common/types/cmsTypes";
import authStore from "../../../stores/auth/authStore";
import contentStore from "../../../stores/cms/contentStore";
import theme from "../../../themes/theme";
import ContentBreadcrumb from "../ContentBreadcrumb";
import RichTextRenderer from "../RichTextRenderer";
import LargeImageArticleHero from "./LargeImageArticleHero";
import SimpleArticleHero from "./SimpleArticleHero";
import StandardArticleHero from "./StandardArticleHero";

export type ArticleProps = {
   id: string;
   article: ContentfulArticle;
};

export const prefaceFont = "text-lg font-light leading-relaxed tracking-normal";

const Article = ({ article, id }: ArticleProps) => {
   // Defaults to false, unless a parent is wraps this component with <BodyOnlyContext.Provider value={true} />
   const articleType = article.fields.articleType;
   const [fullUrl] = useLocation();

   const articleVariantMap = {
      "Standard-utseende": "standard",
      "Stort bilde, header og ingress": "standard",
      "Uten bilde, header og ingress": "simple",
      "Enkel tittel og ingress": "simple"
   }[articleType ?? "Standard-utseende"];
   const articleVariant = articleVariantMap === "standard" ? "standard" : "simple";

   const entryContext: EmbeddedEntryContextType = articleVariant === "simple" ? "simple-article" : "standard-article";
   const seoMetaDescription = article.fields.metaDescription || article.fields.preface;
   const title = article.fields.metaTitle || `${article.fields.title} - ${theme.siteName}`;

   useEffect(() => {
      if (!article.sys.id) {
         return;
      }
      const cleanedFullUrl = fullUrl.startsWith("/") ? fullUrl.slice(1) : fullUrl;
      if (!cleanedFullUrl.startsWith(PROMOTION_SECTION_SLUG)) {
         return;
      }

      contentStore.addViewedPromotionArticleId(article.sys.id);
   }, [article.sys.id, fullUrl]);

   const heroComponents = {
      "Enkel tittel og ingress": SimpleArticleHero,
      "Stort bilde, header og ingress": LargeImageArticleHero,
      "Standard-utseende": StandardArticleHero,
      "Uten bilde, header og ingress": null
   };

   const HeroComponent = articleType ? heroComponents[articleType] : StandardArticleHero;
   const showBreadcrumb = HeroComponent && !authStore.currentlyRequiresLogin.includes("ACCOUNT_PAGES");

   return (
      <>
         <Helmet>
            <title>{title}</title>
            {!!seoMetaDescription && <meta name="description" content={seoMetaDescription} />}
         </Helmet>
         {showBreadcrumb && <ContentBreadcrumb />}
         <div className={cn({ "lg:px-[--article-px]": articleVariant === "standard" })}>
            {HeroComponent && <HeroComponent article={article} id={id} />}

            <div>
               <RichTextRenderer
                  variant={articleVariant}
                  document={article.fields.body}
                  context={entryContext}
                  trackingLocationName="Article"
               />
            </div>
         </div>
      </>
   );
};

export default Article;
